import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Resolver, useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import {
  OutlinedButton,
  PrimaryButton,
  TextInput,
} from "@normasteaching/norma-ui";
import { Layers, Package, Search } from "lucide-react";
import { FullPageLoading } from "../../UI/FullPageLoading.tsx";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { useAllNormasTeachingCoupons } from "../../ProductService/NormasTeachingCoupon/useAllNormasTeachingCoupons.ts";
import { useActivePromosForProduct } from "../../ProductService/NormasTeachingPromo/useActivePromoForProduct.ts";
import { NormasTeachingCouponDTO } from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { CouponType } from "../../ProductService/NormasTeachingCoupon/CouponType.ts";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";
import { isItemDisabled } from "../../ProductService/isItemDisabled.ts";
import { isRateKoVariant } from "../../ProductService/isRateKoVariant.ts";
import { PurchasableItemPickerModal } from "./PurchasableItemPickerModal.tsx";
import { Article7TerSwitch } from "./Article7TerSwitch.tsx";
import { isArticle7Ter } from "../../ProductService/BonificoOrder/article7Ter.ts";
import { InvoiceItemDescriptionInput } from "./InvoiceItemDescriptionInput.tsx";
import {
  chosenInvoiceItemDescription,
  invoiceItemDescriptionPlaceholder,
  standardInvoiceItemDescriptionLabel,
  standardInvoiceItemDescriptionOfItem,
} from "../../ProductService/BonificoOrder/invoiceItemDescription.ts";
import {
  BillingFormFields,
  BillingFormSection,
} from "../../NormasTeachingCheckout/BillingFormSection.tsx";
import { CouponSection } from "../../NormasTeachingCheckout/CouponSection.tsx";
import { businessTypeLabels } from "../../MoneyTransfer/BusinessType.tsx";
import {
  bonificoBillingSchemaFields,
  hasSdiOrPec,
} from "../../BonificoCheckout/bonificoBillingValidation.ts";
import {
  BonificoOrderDTO,
  BonificoOrderItemPayload,
  BonificoParticipant,
  CalendarDate,
  getBonificoOrderErrorMessage,
  VatExemption,
} from "../../ProductService/BonificoOrder/BonificoOrderDTO.ts";
import { useCreateBonificoOrder } from "../../ProductService/BonificoOrder/useCreateBonificoOrder.ts";
import {
  toBonificoBusinessInformation,
  toBonificoCustomerTaxCode,
} from "../../ProductService/BonificoOrder/toBonificoBusinessInformation.ts";
import {
  describeBillingIdentity,
  toBonificoBillingFormFields,
} from "../../ProductService/BonificoOrder/bonificoBilling.ts";
import {
  BonificoPricingPreview,
  calculateBonificoPricingPreview,
} from "../../ProductService/BonificoOrder/calculateBonificoPricingPreview.ts";

export type BonificoOrderFormMode = "create" | "confirm" | "historical";

type FormValues = BillingFormFields & {
  productType: "product" | "bundle";
  productId: string;
  customer: BonificoParticipant;
  participants: BonificoParticipant[];
  bundleParticipants: BonificoParticipant[];
  isAmountManuallySet: boolean;
  amountInEuros?: number;
  receiptDate?: CalendarDate;
  valueDate?: CalendarDate;
};

const participantSchema = yup.object({
  email: yup.string().trim().email("Email non valida").required("Obbligatorio"),
  firstName: yup
    .string()
    .trim()
    .min(2, "Nome troppo corto")
    .required("Obbligatorio"),
  lastName: yup
    .string()
    .trim()
    .min(2, "Cognome troppo corto")
    .required("Obbligatorio"),
});

const optionalBillingSchemaFields = {
  invoiceRequired: yup.boolean().default(false),
  businessType: yup.string().optional(),
  businessName: yup.string().optional(),
  vat: yup.string().optional(),
  taxCode: yup.string().optional(),
  address: yup
    .object({
      street: yup.string().optional(),
      cap: yup.string().optional(),
      city: yup.string().optional(),
      province: yup.string().optional(),
      country: yup.string().optional(),
    })
    .optional(),
  pec: yup.string().optional(),
  sdiCode: yup.string().optional(),
};

function isBillingRequired(
  mode: BonificoOrderFormMode,
  invoiceRequired?: boolean,
): boolean {
  return mode !== "historical" || !!invoiceRequired;
}

function createSchema(mode: BonificoOrderFormMode) {
  return yup.lazy((values: FormValues) => {
    const billingRequired = isBillingRequired(mode, values.invoiceRequired);
    const schema = yup.object({
      productType: yup.string().oneOf(["product", "bundle"]).required(),
      productId: yup.string().required("Seleziona un prodotto o bundle"),
      customer: participantSchema,
      participants: yup.array(participantSchema).default([]),
      bundleParticipants: yup.array(participantSchema).default([]),
      isAmountManuallySet: yup.boolean().default(false),
      amountInEuros: yup.number().when("isAmountManuallySet", {
        is: true,
        then: (s) =>
          s
            .typeError("Inserisci un importo valido")
            .min(0, "L'importo non può essere negativo")
            .required("Importo obbligatorio"),
        otherwise: (s) => s.notRequired(),
      }),
      receiptDate:
        mode === "historical"
          ? yup.string().notRequired()
          : yup.string().required("La data della contabile è obbligatoria"),
      valueDate:
        mode === "historical"
          ? yup.string().required("La data di valuta è obbligatoria")
          : yup.string().notRequired(),
      ...(billingRequired
        ? bonificoBillingSchemaFields
        : optionalBillingSchemaFields),
    });
    return billingRequired
      ? schema.test("has-at-least-sdi-or-pec", "", hasSdiOrPec)
      : schema;
  });
}

function formatEuros(amountInCents: number): string {
  return `€ ${(amountInCents / 100).toFixed(2)}`;
}

function calendarDateToNoonUtc(date?: CalendarDate): string | undefined {
  return date ? `${date}T12:00:00.000Z` : undefined;
}

function draftToDefaultValues(draft?: BonificoOrderDTO): FormValues {
  return {
    productType: draft?.productType ?? "product",
    productId: draft?.normasteachingProductId ?? "",
    customer: {
      email: draft?.customer.email ?? "",
      firstName: draft?.customer.firstName ?? "",
      lastName: draft?.customer.lastName ?? "",
    },
    participants: draft?.participants ?? [],
    bundleParticipants:
      draft?.bundleParticipants?.map((bp) => ({
        email: bp.customer.email,
        firstName: bp.customer.firstName,
        lastName: bp.customer.lastName,
      })) ?? [],
    isAmountManuallySet: false,
    amountInEuros: undefined,
    receiptDate: "",
    valueDate: "",
    ...toBonificoBillingFormFields(draft),
  };
}

function SummaryRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-gray-500 min-w-36 shrink-0">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}

function PromoBadges({
  productId,
  referenceDateUtc,
}: {
  productId: string;
  referenceDateUtc?: string;
}) {
  const { promos } = useActivePromosForProduct(productId, referenceDateUtc);
  if (promos.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {promos.map((promo) => (
        <span
          key={promo._id}
          title={promo.description}
          className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full"
        >
          {promo.name}
        </span>
      ))}
    </div>
  );
}

const MODE_NOTICES: Record<BonificoOrderFormMode, string> = {
  create:
    "Alla conferma verranno assegnati i corsi e il deal HubSpot verrà chiuso alla data della contabile. Fattura e transazione su BigQuery verranno emesse quando segnerai l'ordine come pagato.",
  confirm:
    "Alla conferma verranno assegnati i corsi e il deal HubSpot verrà chiuso alla data della contabile. Fattura e transazione su BigQuery verranno emesse quando segnerai l'ordine come pagato.",
  historical:
    "L'ordine verrà solo inserito su BigQuery alla data di valuta: nessun corso, deal o documento fiscale.",
};

export function BonificoOrderForm({
  mode,
  draft,
  onSaved,
  onCancel,
}: {
  mode: BonificoOrderFormMode;
  draft?: BonificoOrderDTO;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { products, isLoading: loadingProducts } =
    useAllNormasTeachingProducts();
  const { bundles, isLoading: loadingBundles } = useAllNormasTeachingBundles();
  const { coupons, isLoading: isLoadingCoupons } =
    useAllNormasTeachingCoupons();
  const { createConfirmedOrder, confirmDraft, createHistoricalOrder, loading } =
    useCreateBonificoOrder();

  const [appliedCoupon, setAppliedCoupon] =
    useState<NormasTeachingCouponDTO | null>(null);
  const [pendingData, setPendingData] = useState<FormValues | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isArticle7TerApplied, setIsArticle7TerApplied] = useState(
    isArticle7Ter(draft?.vatExemption),
  );
  const [invoiceItemDescription, setInvoiceItemDescription] = useState(
    draft?.invoiceItemDescription ?? "",
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(
      createSchema(mode),
    ) as unknown as Resolver<FormValues>,
    defaultValues: draftToDefaultValues(draft),
  });

  const productType = watch("productType");
  const productId = watch("productId");
  const isAmountManuallySet = watch("isAmountManuallySet");
  const amountInEuros = watch("amountInEuros");
  const invoiceRequired = watch("invoiceRequired");
  const referenceDateUtc = calendarDateToNoonUtc(
    mode === "historical" ? watch("valueDate") : watch("receiptDate"),
  );

  const {
    fields: participantFields,
    append: appendParticipant,
    remove: removeParticipant,
  } = useFieldArray({ control, name: "participants" });
  const { fields: bundleParticipantFields } = useFieldArray({
    control,
    name: "bundleParticipants",
  });

  const productMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p._id, p])),
    [products],
  );
  const selectedProduct =
    productType === "product" ? productMap[productId] : undefined;
  const selectedBundle =
    productType === "bundle"
      ? bundles.find((b) => b._id === productId)
      : undefined;
  const standardInvoiceItemDescription = standardInvoiceItemDescriptionOfItem(
    selectedProduct ?? selectedBundle,
  );

  useEffect(() => {
    setAppliedCoupon(null);
  }, [productId, productType]);

  useEffect(() => {
    if (productType === "product" && (selectedProduct?.peopleCount ?? 1) <= 1)
      setValue("participants", []);
  }, [productType, selectedProduct, setValue]);

  useEffect(() => {
    if (!selectedBundle) return;
    const slots = selectedBundle.bundleProducts.length;
    const current = watch("bundleParticipants") ?? [];
    if (current.length === slots) return;
    setValue(
      "bundleParticipants",
      Array.from(
        { length: slots },
        (_, i) => current[i] ?? { email: "", firstName: "", lastName: "" },
      ),
    );
  }, [selectedBundle, setValue, watch]);

  const bundleLines = (selectedBundle?.bundleProducts ?? []).flatMap(
    (slot, slotIndex) =>
      slot.products.map((p) => ({
        slotIndex,
        productId: p.id,
        listPriceInCents: p.priceInCents.amount,
      })),
  );
  const listPriceInCents =
    selectedProduct?.priceInCents.amount ?? selectedBundle?.priceInCents.amount;
  const pricing: BonificoPricingPreview | undefined =
    listPriceInCents !== undefined
      ? calculateBonificoPricingPreview({
          listPriceInCents,
          coupon: appliedCoupon,
          amountOverrideInCents:
            isAmountManuallySet && Number.isFinite(amountInEuros)
              ? Math.round((amountInEuros ?? 0) * 100)
              : undefined,
          bundleProductListPricesInCents: bundleLines.map(
            (l) => l.listPriceInCents,
          ),
        })
      : undefined;

  const onSubmit = (data: FormValues) => {
    setSubmitError(null);
    setPendingData(data);
  };

  const save = async () => {
    if (!pendingData) return;
    const data = pendingData;
    const item: BonificoOrderItemPayload = {
      productType: data.productType,
      normasteachingProductId: data.productId,
      participants:
        data.productType === "product" ? data.participants : undefined,
      bundleParticipants:
        data.productType === "bundle" ? data.bundleParticipants : undefined,
      couponId: appliedCoupon?._id,
      amountOverrideInCents:
        data.isAmountManuallySet && data.amountInEuros !== undefined
          ? Math.round(data.amountInEuros * 100)
          : undefined,
    };
    const customer = {
      ...data.customer,
      taxCode: toBonificoCustomerTaxCode(data),
    };
    try {
      if (mode === "historical") {
        await createHistoricalOrder({
          ...item,
          customer,
          businessInformation: data.invoiceRequired
            ? toBonificoBusinessInformation(data)
            : undefined,
          valueDate: data.valueDate!,
        });
      } else {
        const payload = {
          ...item,
          customer,
          businessInformation: toBonificoBusinessInformation(data),
          receiptDate: data.receiptDate!,
          ...(isArticle7TerApplied && {
            vatExemption: VatExemption.Article7Ter,
          }),
          invoiceItemDescription: chosenInvoiceItemDescription(
            invoiceItemDescription,
          ),
        };
        if (mode === "confirm") await confirmDraft(draft!.orderId, payload);
        else await createConfirmedOrder(payload);
      }
      onSaved();
    } catch (e) {
      setPendingData(null);
      setSubmitError(
        getBonificoOrderErrorMessage(
          e,
          "Errore durante il salvataggio dell'ordine. Riprova.",
        ),
      );
    }
  };

  if (loadingProducts || loadingBundles) return <FullPageLoading />;

  const isPickable = (item: { isEnabled?: boolean }, variantName: string) =>
    (mode === "historical" || !isItemDisabled(item)) &&
    !isRateKoVariant(variantName);
  const pickableProducts = products.filter((p) =>
    isPickable(p, p.variantProductName),
  );
  const pickableBundles = bundles.filter((b) =>
    isPickable(b, b.variantBundleName),
  );
  const selectedItem = selectedProduct
    ? {
        name: selectedProduct.productName,
        variantName: selectedProduct.variantProductName,
        priceInCents: selectedProduct.priceInCents.amount,
      }
    : selectedBundle
      ? {
          name: selectedBundle.bundleName,
          variantName: selectedBundle.variantBundleName,
          priceInCents: selectedBundle.priceInCents.amount,
        }
      : undefined;

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        <div
          className={`rounded-xl border p-4 text-sm ${
            mode === "historical"
              ? "bg-orange-50 border-orange-300 text-orange-800"
              : "bg-blue-50 border-blue-200 text-blue-800"
          }`}
        >
          {MODE_NOTICES[mode]}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-semibold text-lg">Prodotto acquistato *</h2>
            <OutlinedButton
              iconLeft={<Search size={18} />}
              label={selectedItem ? "Cambia" : "Seleziona"}
              onClick={() => setIsPickerOpen(true)}
            />
          </div>
          {selectedItem ? (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              {productType === "product" ? (
                <Package size={18} className="text-blue-500 shrink-0" />
              ) : (
                <Layers size={18} className="text-purple-500 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{selectedItem.name}</p>
                <p className="text-sm text-gray-500">
                  {selectedItem.variantName} —{" "}
                  {formatEuros(selectedItem.priceInCents)}
                </p>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  productType === "product"
                    ? "bg-blue-100 text-blue-700"
                    : "bg-purple-100 text-purple-700"
                }`}
              >
                {productType === "product" ? "Prodotto" : "Bundle"}
              </span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Nessun prodotto o bundle selezionato.
            </p>
          )}
          {errors.productId && (
            <p className="text-xs text-red-500">{errors.productId.message}</p>
          )}
          {selectedProduct && (
            <PromoBadges
              productId={selectedProduct._id}
              referenceDateUtc={referenceDateUtc}
            />
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
          <h2 className="font-semibold text-lg">Cliente acquirente</h2>
          <p className="text-xs text-gray-500">
            Il deal HubSpot viene cercato con l'email del cliente acquirente.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(["email", "firstName", "lastName"] as const).map((key) => (
              <div key={key} className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {
                    { email: "Email", firstName: "Nome", lastName: "Cognome" }[
                      key
                    ]
                  }{" "}
                  *
                </label>
                <TextInput
                  {...register(`customer.${key}`)}
                  errorMessage={errors.customer?.[key]?.message}
                />
              </div>
            ))}
          </div>
        </div>

        {selectedProduct && selectedProduct.peopleCount > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-lg">Partecipanti aggiuntivi</h2>
              <button
                type="button"
                onClick={() =>
                  appendParticipant({ email: "", firstName: "", lastName: "" })
                }
                className="text-sm text-blue-600 hover:underline"
              >
                + Aggiungi partecipante
              </button>
            </div>
            {participantFields.map((field, idx) => (
              <div
                key={field.id}
                className="border border-gray-100 rounded-lg p-3 flex flex-col gap-3"
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">
                    Partecipante {idx + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeParticipant(idx)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Rimuovi
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(["email", "firstName", "lastName"] as const).map((key) => (
                    <TextInput
                      key={key}
                      placeholder={
                        {
                          email: "Email",
                          firstName: "Nome",
                          lastName: "Cognome",
                        }[key]
                      }
                      {...register(`participants.${idx}.${key}`)}
                      errorMessage={errors.participants?.[idx]?.[key]?.message}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedBundle && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-4">
            <h2 className="font-semibold text-lg">Partecipanti bundle</h2>
            {bundleParticipantFields.map((field, slotIndex) => (
              <div
                key={field.id}
                className="border border-gray-100 rounded-lg p-4 flex flex-col gap-4"
              >
                <h3 className="text-sm font-semibold text-gray-700">
                  Slot {slotIndex + 1}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {(["email", "firstName", "lastName"] as const).map((key) => (
                    <TextInput
                      key={key}
                      placeholder={
                        {
                          email: "Email *",
                          firstName: "Nome *",
                          lastName: "Cognome *",
                        }[key]
                      }
                      {...register(`bundleParticipants.${slotIndex}.${key}`)}
                      errorMessage={
                        errors.bundleParticipants?.[slotIndex]?.[key]?.message
                      }
                    />
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Prodotti inclusi
                  </p>
                  {bundleLines.map((line, lineIndex) =>
                    line.slotIndex !== slotIndex ? null : (
                      <div
                        key={lineIndex}
                        className="flex items-start justify-between gap-3 text-sm text-gray-700"
                      >
                        <div className="flex flex-col flex-1 min-w-0">
                          <span>
                            {productMap[line.productId]?.variantProductName ??
                              line.productId}
                          </span>
                          <PromoBadges
                            productId={line.productId}
                            referenceDateUtc={referenceDateUtc}
                          />
                        </div>
                        <span className="flex items-center gap-2 shrink-0">
                          {pricing &&
                            pricing.bundleProductAmountsInCents[lineIndex] !==
                              line.listPriceInCents && (
                              <span className="text-gray-400 line-through">
                                {formatEuros(line.listPriceInCents)}
                              </span>
                            )}
                          <span className="font-medium text-gray-900">
                            {formatEuros(
                              pricing?.bundleProductAmountsInCents[lineIndex] ??
                                line.listPriceInCents,
                            )}
                          </span>
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {productId && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <CouponSection
              allCoupons={coupons}
              isLoadingCoupons={isLoadingCoupons}
              currentCoupon={appliedCoupon ?? undefined}
              onCouponApplied={setAppliedCoupon}
              onCouponRemoved={() => setAppliedCoupon(null)}
              itemId={productId}
              itemType={
                productType === "product"
                  ? NormasTeachingItemType.Product
                  : NormasTeachingItemType.Bundle
              }
              validityCheckDate={
                referenceDateUtc ? new Date(referenceDateUtc) : undefined
              }
            />
            {mode !== "historical" && (
              <p className="text-xs text-gray-500 mt-2">
                La validità del coupon viene verificata alla data della
                contabile.
              </p>
            )}
          </div>
        )}

        {pricing && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-3">
            <h2 className="font-semibold text-lg">Importo</h2>
            <SummaryRow
              label="Prezzo di listino"
              value={formatEuros(pricing.listPriceInCents)}
            />
            {appliedCoupon && (
              <SummaryRow
                label={`Coupon ${appliedCoupon.couponCode}`}
                value={
                  <span className="text-green-700">
                    -{formatEuros(pricing.couponDiscountInCents)}
                  </span>
                }
              />
            )}
            <SummaryRow
              label="Importo pagato"
              value={
                <span className="font-semibold">
                  {formatEuros(pricing.amountPaidInCents)}
                </span>
              }
            />
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register("isAmountManuallySet")}
                onChange={(e) => {
                  setValue("isAmountManuallySet", e.target.checked);
                  setValue(
                    "amountInEuros",
                    e.target.checked
                      ? pricing.amountPaidInCents / 100
                      : undefined,
                  );
                }}
                className="w-4 h-4 accent-primary"
              />
              <span className="text-sm font-medium text-gray-700">
                Modifica l'importo pagato (sconti speciali)
              </span>
            </label>
            {isAmountManuallySet && (
              <div className="flex flex-col gap-1 max-w-xs">
                <TextInput
                  type="number"
                  step="0.01"
                  min="0"
                  {...register("amountInEuros", { valueAsNumber: true })}
                  errorMessage={errors.amountInEuros?.message}
                />
                {productType === "bundle" && (
                  <p className="text-xs text-gray-500">
                    L'importo viene ripartito sui prodotti del bundle in
                    proporzione al loro prezzo.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2">
          {mode === "historical" ? (
            <>
              <label className="text-sm font-medium text-gray-700">
                Data di valuta *
              </label>
              <input
                type="date"
                max={dayjs().format("YYYY-MM-DD")}
                {...register("valueDate")}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 max-w-xs"
              />
              {errors.valueDate && (
                <p className="text-xs text-red-500">
                  {errors.valueDate.message}
                </p>
              )}
            </>
          ) : (
            <>
              <label className="text-sm font-medium text-gray-700">
                Data della contabile *
              </label>
              <input
                type="date"
                max={dayjs().format("YYYY-MM-DD")}
                {...register("receiptDate")}
                className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 max-w-xs"
              />
              {errors.receiptDate && (
                <p className="text-xs text-red-500">
                  {errors.receiptDate.message}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Il deal HubSpot viene chiuso a questa data, e promo e coupon
                vengono valutati a questa data.
              </p>
            </>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {mode !== "historical" && (
            <h2 className="font-semibold text-lg">Dati fatturazione</h2>
          )}
          <BillingFormSection
            control={control}
            register={register}
            errors={errors}
            invoiceRequired={isBillingRequired(mode, invoiceRequired)}
            businessType={watch("businessType")}
            taxCode={watch("taxCode")}
            isBusinessInformationRequired={true}
            isInvoiceMandatory={mode !== "historical"}
            trigger={trigger}
            askBillingCountry
            onArticle7TerSuggestion={setIsArticle7TerApplied}
          />
          {mode !== "historical" && (
            <div className="mt-4 flex flex-col gap-4">
              <Article7TerSwitch
                checked={isArticle7TerApplied}
                onChange={setIsArticle7TerApplied}
              />
              <InvoiceItemDescriptionInput
                value={invoiceItemDescription}
                placeholder={invoiceItemDescriptionPlaceholder(
                  standardInvoiceItemDescription,
                )}
                onChange={setInvoiceItemDescription}
              />
            </div>
          )}
        </div>

        {submitError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {submitError}
          </p>
        )}

        <div className="flex gap-3 pb-8">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
          >
            Annulla
          </button>
          <PrimaryButton
            label={
              mode === "historical"
                ? "Registra ordine storico"
                : "Conferma ordine"
            }
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
          />
        </div>
      </form>

      <PurchasableItemPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        products={pickableProducts}
        bundles={pickableBundles}
        selected={productId ? { type: productType, id: productId } : undefined}
        onPick={(item) => {
          setValue("productType", item.type);
          setValue("productId", item.id, { shouldValidate: true });
        }}
      />

      {pendingData && pricing && (
        <Modal
          isOpen
          onClose={() => setPendingData(null)}
          title="Riepilogo ordine"
          overridePanel={({ title, children }) => (
            <Dialog.Panel className="flex-1 md:flex-auto md:w-full md:max-w-2xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
              <Dialog.Title
                as="h3"
                className="text-lg font-semibold leading-6 text-gray-900 mb-4"
              >
                {title}
              </Dialog.Title>
              {children}
            </Dialog.Panel>
          )}
        >
          <div className="flex flex-col gap-3 max-h-[70vh] overflow-y-auto pr-1">
            <p className="text-sm text-gray-700">{MODE_NOTICES[mode]}</p>
            <SummaryRow
              label={
                pendingData.productType === "product" ? "Prodotto" : "Bundle"
              }
              value={
                selectedProduct?.variantProductName ??
                selectedBundle?.variantBundleName ??
                pendingData.productId
              }
            />
            <SummaryRow
              label="Importo pagato"
              value={`${formatEuros(pricing.amountPaidInCents)}${
                pendingData.isAmountManuallySet ? " (modificato a mano)" : ""
              }`}
            />
            {appliedCoupon && (
              <SummaryRow
                label="Coupon"
                value={`${appliedCoupon.couponCode} (${
                  appliedCoupon.value.couponType === CouponType.Percentage
                    ? `-${appliedCoupon.value.amount}%`
                    : `-${formatEuros(appliedCoupon.value.amount)}`
                })`}
              />
            )}
            <SummaryRow
              label="Cliente"
              value={`${pendingData.customer.firstName} ${pendingData.customer.lastName} — ${pendingData.customer.email}`}
            />
            {pendingData.productType === "product" &&
              pendingData.participants.map((p, i) => (
                <SummaryRow
                  key={i}
                  label={`Partecipante ${i + 1}`}
                  value={`${p.firstName} ${p.lastName} — ${p.email}`}
                />
              ))}
            {pendingData.productType === "bundle" &&
              pendingData.bundleParticipants.map((p, i) => (
                <SummaryRow
                  key={i}
                  label={`Slot ${i + 1}`}
                  value={`${p.firstName} ${p.lastName} — ${p.email}`}
                />
              ))}
            <SummaryRow
              label={mode === "historical" ? "Data valuta" : "Data contabile"}
              value={dayjs(
                mode === "historical"
                  ? pendingData.valueDate
                  : pendingData.receiptDate,
              ).format("DD/MM/YYYY")}
            />
            <SummaryRow
              label="Fatturazione"
              value={
                isBillingRequired(mode, pendingData.invoiceRequired)
                  ? `${businessTypeLabels[pendingData.businessType ?? ""] ?? ""} — ${describeBillingIdentity(pendingData)}`
                  : "Nessun dato di fatturazione"
              }
            />
            {mode !== "historical" && (
              <>
                <SummaryRow
                  label="IVA"
                  value={
                    isArticle7TerApplied
                      ? "Articolo 7 ter (operazione non soggetta, 0%)"
                      : "Ordinaria al 22%"
                  }
                />
                <SummaryRow
                  label="Descrizione in fattura"
                  value={
                    chosenInvoiceItemDescription(invoiceItemDescription) ??
                    standardInvoiceItemDescriptionLabel(
                      standardInvoiceItemDescription,
                    )
                  }
                />
              </>
            )}
          </div>
          <div className="flex gap-3 mt-6 justify-end">
            <button
              type="button"
              onClick={() => setPendingData(null)}
              className="px-4 py-2 rounded-lg text-sm border border-gray-300 hover:bg-gray-50"
            >
              Indietro
            </button>
            <button
              type="button"
              onClick={save}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm bg-primary text-white font-semibold hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Salvataggio in corso..." : "Conferma"}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
